/**
 * The workspace's audit log, read from the activity feed (GET /activity, design 5.2): configuration
 * changes only, filtered by kind, person and date. The API decides what the caller may see; this only
 * names things. List pages link here already filtered to their kind.
 */
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ActivityEntry } from "@workcore/schemas";
import { get } from "./api";
import { parsePrincipal, useContainers, useDefinitions, useDirectory, wsKey } from "./directory";
import { EmptyState, ErrorBox, PageHeader, When } from "./ui";

const KIND: Record<string, string> = {
  user: "user",
  group: "group",
  service_account: "service account",
  api_key: "API key",
  role_assignment: "role",
  container: "project",
  acl: "access",
  invitation: "invitation",
  webhook: "webhook",
  definition: "workflow",
  object_type: "type",
  state: "state",
  field: "field",
};

/** What the filter offers, each a set of record kinds. Object content (objects, comments) is not configuration. */
export const AUDIT_KINDS: { key: string; label: string; kinds: string[] }[] = [
  { key: "users", label: "People", kinds: ["user", "invitation"] },
  { key: "groups", label: "Groups", kinds: ["group"] },
  { key: "roles", label: "Roles", kinds: ["role_assignment"] },
  { key: "service-accounts", label: "Service accounts and keys", kinds: ["service_account", "api_key"] },
  { key: "projects", label: "Projects and access", kinds: ["container", "acl"] },
  { key: "workflows", label: "Workflows", kinds: ["definition", "object_type", "state", "field"] },
  { key: "webhooks", label: "Webhooks", kinds: ["webhook"] },
];
const ALL_KINDS = AUDIT_KINDS.flatMap((k) => k.kinds);
const PRINCIPAL = /^(user|group|service_account):\d+$/;
const PAGE = 50;

function useNames(slug: string) {
  const dir = useDirectory(slug);
  const containers = useContainers(slug).data ?? [];
  const defs = useDefinitions(slug).data ?? [];
  const scope = (s: string) => {
    const [kind, id] = s.split(":");
    if (kind === "container") return containers.find((c) => c.id === Number(id))?.name ?? `project #${id}`;
    if (kind === "definition") return defs.find((d) => d.id === Number(id))?.name ?? `workflow #${id}`;
    if (kind === "object") return `object #${id}`;
    return "workspace";
  };
  const value = (v: unknown): string => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string") {
      if (PRINCIPAL.test(v)) return dir.label(parsePrincipal(v));
      return v.length > 60 ? `${v.slice(0, 60)}…` : v;
    }
    return JSON.stringify(v);
  };
  const actor = (e: ActivityEntry) =>
    e.actorKind === "system" ? "system" : dir.label({ kind: e.actorKind as "user" | "service_account", id: e.actorId ?? 0 });
  const thing = (e: ActivityEntry) => {
    const ch = e.changes as Record<string, { old?: unknown; new?: unknown }>;
    const pick = (k: string) => (ch[k]?.new ?? ch[k]?.old) as string | undefined;
    switch (e.entityKind) {
      case "user":
        return dir.label({ kind: "user", id: e.entityId });
      case "group":
        return dir.label({ kind: "group", id: e.entityId });
      case "service_account":
        return dir.label({ kind: "service_account", id: e.entityId });
      case "role_assignment":
        return `${value(pick("principal"))} as ${String(pick("role") ?? "?").replace(/_/g, " ")} on ${scope(pick("scope") ?? "workspace")}`;
      case "webhook":
        return pick("name") ?? `webhook #${e.entityId}`;
      case "api_key":
        return `key #${e.entityId}`;
      case "container":
        return containers.find((c) => c.id === e.entityId)?.name ?? `project #${e.entityId}`;
      case "acl":
        return `${value(pick("principal"))} on ${scope(pick("scope") ?? "")}`;
      case "definition":
        return defs.find((d) => d.id === e.entityId)?.name ?? pick("name") ?? `workflow #${e.entityId}`;
      case "object_type":
      case "state":
      case "field":
        return pick("name") ?? pick("label") ?? pick("key") ?? `#${e.entityId}`;
      default:
        return `#${e.entityId}`;
    }
  };
  /** a value named by what its key refers to */
  const valueOf = (k: string, v: unknown): string => {
    if (typeof v === "number" && (k === "ownerId" || k === "userId" || k === "ownerUserId")) return dir.label({ kind: "user", id: v });
    if (typeof v === "number" && (k === "definition_id" || k === "definitionId")) return defs.find((d) => d.id === v)?.name ?? `workflow #${v}`;
    return value(v);
  };
  return { actor, thing, value: valueOf };
}

function Changes({ e, value }: { e: ActivityEntry; value: (k: string, v: unknown) => string }) {
  // role and access records are described in full by `thing`, apart from the permission set
  const skip = new Set(e.entityKind === "role_assignment" ? ["principal", "role", "scope"] : e.entityKind === "acl" ? ["principal", "scope"] : []);
  const parts = Object.entries(e.changes)
    .filter(([k]) => !skip.has(k))
    .map(([k, c]) => {
      const x = c as { old?: unknown; new?: unknown; added?: unknown[]; removed?: unknown[] };
      if (x.added || x.removed) {
        const list = (a: unknown[] | undefined, sign: string) => (a?.length ? `${sign}${a.map((v) => value(k, v)).join(", ")}` : "");
        return `${k}: ${[list(x.added, "+"), list(x.removed, "−")].filter(Boolean).join(" ")}`;
      }
      const none = (v: unknown) => v === null || v === undefined;
      if (none(x.old) && none(x.new)) return "";
      if (none(x.old)) return `${k} = ${value(k, x.new)}`;
      if (none(x.new)) return `${k} ${value(k, x.old)} removed`;
      return `${k}: ${value(k, x.old)} → ${value(k, x.new)}`;
    })
    .filter(Boolean);
  return <span className="muted small">{parts.join(" · ")}</span>;
}

function Entries({ slug, kinds, actor, since }: { slug: string; kinds: string[]; actor?: string; since?: string }) {
  const n = useNames(slug);
  const q = useInfiniteQuery({
    queryKey: [...wsKey(slug, "activity"), kinds.join(","), actor ?? "", since ?? ""],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) =>
      z.array(ActivityEntry).parse(
        await get(`/v1/ws/${slug}/activity`, { entity_kind: kinds.join(","), limit: PAGE, before: pageParam, ...(actor ? { actor } : {}), ...(since ? { since: new Date(since).toISOString() } : {}) }),
      ),
    getNextPageParam: (last) => (last.length === PAGE ? last.at(-1)?.id : undefined),
  });
  const entries = q.data?.pages.flat() ?? [];
  return (
    <>
      <ErrorBox error={q.error} />
      {entries.length > 0 && (
        <ol className="timeline">
          {entries.map((e) => (
            <li key={e.id}>
              <When at={e.at} /> <strong>{n.actor(e)}</strong> {e.action.replace(/_/g, " ")} <span className="tag">{KIND[e.entityKind] ?? e.entityKind.replace(/_/g, " ")}</span> {n.thing(e)} <Changes e={e} value={n.value} />
            </li>
          ))}
        </ol>
      )}
      {entries.length === 0 && !q.isLoading && <EmptyState title="Nothing recorded" body="No changes match these filters. Try a wider date range or another kind." />}
      {q.hasNextPage && <button className="secondary" onClick={() => void q.fetchNextPage()}>Older</button>}
    </>
  );
}

/** A list page's link to its own slice of the audit log. */
export function HistoryLink({ slug, kind }: { slug: string; kind: string }) {
  return (
    <Link className="button secondary" to={`/w/${slug}/audit?kind=${kind}`}>
      History
    </Link>
  );
}

export function AuditLogPage() {
  const { slug = "" } = useParams();
  const dir = useDirectory(slug);
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "";
  const actor = params.get("actor") ?? "";
  const since = params.get("since") ?? "";
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const kinds = AUDIT_KINDS.find((k) => k.key === kind)?.kinds ?? ALL_KINDS;
  return (
    <>
      <PageHeader title="Audit log" description="Every configuration change in this workspace, newest first: who did it, when, and the values before and after." />
      <form className="inline wrap filters" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <label className="inline-field">
          <span>What</span>
          <select aria-label="Kind of change" value={kind} onChange={(e) => set("kind", e.target.value)}>
            <option value="">Everything</option>
            {AUDIT_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </label>
        <label className="inline-field">
          <span>Who</span>
          <select aria-label="Changed by" value={actor} onChange={(e) => set("actor", e.target.value)}>
            <option value="">Anyone</option>
            {dir.options.filter((o) => !o.value.startsWith("group:")).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="inline-field">
          <span>Since</span>
          <input type="date" aria-label="Since" value={since} onChange={(e) => set("since", e.target.value)} />
        </label>
      </form>
      <section className="panel">
        <Entries slug={slug} kinds={kinds} {...(actor ? { actor } : {})} {...(since ? { since } : {})} />
      </section>
    </>
  );
}
